'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useMemo, useState } from 'react';
import { History, Sparkles } from 'lucide-react';
import { useQueryState } from 'nuqs';
import z from 'zod';
import { useI18n } from '~/features/i18n';
import { cn } from '~/utils';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { useAppForm } from '~/features/form/hooks';
import { SubmitButton } from '~/features/form/subscribers';
import { useActivityAiActiveChat } from './useActivityAiActiveChat';
import { AI_SEARCH_EVENTS, useAiSearchAnalytics } from './ai-search-analytics';
import { useActivityAiChatStore } from './activity-ai-chat-store';
import { RecentChatsList } from './RecentChatsList';
import { SUGGESTION_DESCRIPTORS } from './ai-search-suggestions';
import { ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS, ACTIVITY_SEARCH_MODE_QUERY_PARAMS } from './shared';
import { useActivitySearchMode } from './useActivitySearchMode';
import { AiSearchBar } from './AiSearchBar';
const AI_PROMPT_VALIDATOR = z.object({
  prompt: z.string().trim().min(1)
});

/** AI search entry UI (parallel to pill filter bar). Wires up later to retrieval / streaming. */
export const ActivityAiSearchFilterPanel: React.FC<{
  className?: string;
  /** Visual surface the panel sits on. 'dark' = hero (default), 'light' = white card (e.g. NotFound). */
  surface?: 'dark' | 'light';
}> = t0 => {
  const $ = _c(136);
  const {
    className,
    surface: t1
  } = t0;
  const surface = t1 === undefined ? "dark" : t1;
  const i18n = useI18n();
  const isLight = surface === "light";
  const {
    mode,
    setMode
  } = useActivitySearchMode();
  const aiChat = useActivityAiActiveChat();
  const analytics = useAiSearchAnalytics();
  const threads = useActivityAiChatStore(_temp);
  let t2;
  if ($[0] !== threads) {
    t2 = Object.values(threads).sort(_temp2);
    $[0] = threads;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sortedThreads = t2;
  const [historyOpen, setHistoryOpen] = useState(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ACTIVITY_SEARCH_MODE_QUERY_PARAMS.startPrompt.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [startPromptParam, setStartPromptParam] = useQueryState("startPrompt", t3);
  const [seededPrompt, setSeededPrompt] = useState("");
  let t4;
  if ($[3] !== startPromptParam) {
    t4 = startPromptParam?.trim();
    $[3] = startPromptParam;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const startPrompt = t4;
  if (startPrompt && startPrompt !== seededPrompt) {
    setSeededPrompt(startPrompt);
  }
  let t5;
  if ($[5] !== aiChat || $[6] !== setMode) {
    t5 = prompt => {
      setMode("ai");
      if (aiChat.chatId.length > 0) {
        aiChat.startNewChat(prompt);
      } else {
        aiChat.sendMessage(prompt);
      }
    };
    $[5] = aiChat;
    $[6] = setMode;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handlePromptSubmit = t5;
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      prompt: ""
    };
    t7 = {
      onChange: AI_PROMPT_VALIDATOR,
      onSubmit: AI_PROMPT_VALIDATOR
    };
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  const form = useAppForm({
    defaultValues: t6,
    validators: t7,
    onSubmit(t8) {
      const {
        value
      } = t8;
      if (isLight) {
        analytics[AI_SEARCH_EVENTS.EMPTY_STATE_HANDOFF]({
          session_id: null
        });
      }
      setMode("ai");
      if (aiChat.chatId.length > 0) {
        aiChat.startNewChat(value.prompt);
      } else {
        aiChat.sendMessage(value.prompt);
      }
      form.setFieldValue("prompt", "");
    }
  });
  let t9;
  if ($[10] !== form || $[11] !== setMode || $[12] !== setStartPromptParam || $[13] !== startPrompt) {
    t9 = () => {
      if (!startPrompt) {
        return;
      }
      form.setFieldValue("prompt", startPrompt);
      setMode("ai");
      setStartPromptParam(null);
    };
    $[10] = form;
    $[11] = setMode;
    $[12] = setStartPromptParam;
    $[13] = startPrompt;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== form || $[16] !== setMode || $[17] !== setStartPromptParam || $[18] !== startPrompt) {
    t10 = [startPrompt, setMode, setStartPromptParam, form];
    $[15] = form;
    $[16] = setMode;
    $[17] = setStartPromptParam;
    $[18] = startPrompt;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  useLayoutEffect(t9, t10);
  if (!isLight) {
    let t11;
    if ($[20] !== className) {
      t11 = cn("flex w-full max-w-xl items-center rounded-full bg-white p-2 shadow-2xl shadow-black/5", className);
      $[20] = className;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== setMode) {
      t12 = next => {
        setMode(next);
        if (next !== "ai" && typeof window !== "undefined") {
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }
      };
      $[22] = setMode;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] !== analytics) {
      t13 = next_0 => {
        if (next_0) {
          analytics[AI_SEARCH_EVENTS.HISTORY_OPENED]({
            session_id: null,
            source: "hero"
          });
        }
        setHistoryOpen(next_0);
      };
      $[24] = analytics;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = cn("flex size-9 shrink-0 items-center justify-center rounded-full text-stone-500 transition-colors", "hover:bg-stone-100 hover:text-stone-800", "data-[state=open]:bg-stone-100 data-[state=open]:text-stone-800");
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    let t15;
    if ($[27] !== i18n) {
      t15 = i18n._("activity.search.ai.historyTrigger", undefined, {
        message: "Previous chats"
      });
      $[27] = i18n;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    let t16;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <History className="size-4" strokeWidth={2} aria-hidden={true} />;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    let t17;
    if ($[30] !== t15) {
      t17 = <PopoverTrigger asChild={true}><button type="button" className={t14} aria-label={t15}>{t16}</button></PopoverTrigger>;
      $[30] = t15;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    let t18;
    if ($[32] !== i18n) {
      t18 = i18n._("activity.search.ai.historyHeading", undefined, {
        message: "Recent chats"
      });
      $[32] = i18n;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    let t19;
    if ($[34] !== t18) {
      t19 = <div className="px-3 py-2"><p className="text-xs font-semibold text-foreground">{t18}</p></div>;
      $[34] = t18;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    let t20;
    if ($[36] !== aiChat || $[37] !== analytics || $[38] !== i18n || $[39] !== setMode || $[40] !== sortedThreads) {
      t20 = sortedThreads.length === 0 ? <p className="px-3 py-4 text-center text-xs text-muted-foreground">{i18n._("activity.search.ai.historyPreviewNotice", undefined, {
          message: "Preview only \u2014 history is not saved yet."
        })}</p> : <RecentChatsList threads={sortedThreads} activeThreadId={aiChat.chatId} showSearch={true} variant="popover" className="px-3 py-2" onSelect={id => {
        analytics[AI_SEARCH_EVENTS.CHAT_RESUMED]({
          session_id: id,
          source: "hero_popover"
        });
        setHistoryOpen(false);
        setMode("ai");
        aiChat.openThread(id);
      }} />;
      $[36] = aiChat;
      $[37] = analytics;
      $[38] = i18n;
      $[39] = setMode;
      $[40] = sortedThreads;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    let t21;
    if ($[42] !== t19 || $[43] !== t20) {
      t21 = <PopoverContent align="end" sideOffset={12} className="w-[min(100vw-2rem,22rem)] p-0 shadow-lg">{t19}{t20}</PopoverContent>;
      $[42] = t19;
      $[43] = t20;
      $[44] = t21;
    } else {
      t21 = $[44];
    }
    let t22;
    if ($[45] !== historyOpen || $[46] !== t13 || $[47] !== t17 || $[48] !== t21) {
      t22 = <Popover open={historyOpen} onOpenChange={t13}>{t17}{t21}</Popover>;
      $[45] = historyOpen;
      $[46] = t13;
      $[47] = t17;
      $[48] = t21;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== i18n) {
      t23 = i18n._("activity.search.ai.placeholderPrefix", undefined, {
        message: "Zoek naar"
      });
      $[50] = i18n;
      $[51] = t23;
    } else {
      t23 = $[51];
    }
    let t24;
    if ($[52] !== i18n) {
      t24 = SUGGESTION_DESCRIPTORS.map(s_0 => i18n._(s_0.id, undefined, {
        message: s_0.message
      }));
      $[52] = i18n;
      $[53] = t24;
    } else {
      t24 = $[53];
    }
    let t25;
    if ($[54] !== i18n) {
      t25 = i18n._("activity.search.ai.searchLabel", undefined, {
        message: "AI zoeken"
      });
      $[54] = i18n;
      $[55] = t25;
    } else {
      t25 = $[55];
    }
    let t26;
    if ($[56] !== i18n) {
      t26 = i18n._("activity.search.ai.mobileCtaStart", undefined, {
        message: "Zoeken met AI"
      });
      $[56] = i18n;
      $[57] = t26;
    } else {
      t26 = $[57];
    }
    let t27;
    if ($[58] !== handlePromptSubmit || $[59] !== mode || $[60] !== seededPrompt || $[61] !== t12 || $[62] !== t22 || $[63] !== t23 || $[64] !== t24 || $[65] !== t25 || $[66] !== t26) {
      t27 = <AiSearchBar mode={mode} onModeChange={t12} onSubmit={handlePromptSubmit} initialValue={seededPrompt} trailing={t22} placeholderPrefix={t23} suggestions={t24} aiSearchLabel={t25} ariaLabel={t26} />;
      $[58] = handlePromptSubmit;
      $[59] = mode;
      $[60] = seededPrompt;
      $[61] = t12;
      $[62] = t22;
      $[63] = t23;
      $[64] = t24;
      $[65] = t25;
      $[66] = t26;
      $[67] = t27;
    } else {
      t27 = $[67];
    }
    let t28;
    if ($[68] !== t11 || $[69] !== t27) {
      t28 = <div className={t11}>{t27}</div>;
      $[68] = t11;
      $[69] = t27;
      $[70] = t28;
    } else {
      t28 = $[70];
    }
    return t28;
  }
  let t11;
  if ($[71] !== className) {
    t11 = cn("flex w-full flex-col items-center gap-2 sm:gap-3", className);
    $[71] = className;
    $[72] = t11;
  } else {
    t11 = $[72];
  }
  let t12;
  if ($[73] !== form) {
    t12 = e => {
      e.preventDefault();
      return form.handleSubmit();
    };
    $[73] = form;
    $[74] = t12;
  } else {
    t12 = $[74];
  }
  const t13 = isLight && "border border-stone-200/90";
  let t14;
  if ($[75] !== t13) {
    t14 = cn("relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl shadow-black/5 sm:p-4", t13);
    $[75] = t13;
    $[76] = t14;
  } else {
    t14 = $[76];
  }
  let t15;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Sparkles className="size-3.5 shrink-0 text-secondary sm:size-4" strokeWidth={2} aria-hidden={true} />;
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  let t16;
  if ($[78] !== i18n) {
    t16 = i18n._("activity.search.ai.newPrompt", undefined, {
      message: "New search"
    });
    $[78] = i18n;
    $[79] = t16;
  } else {
    t16 = $[79];
  }
  let t17;
  if ($[80] !== t16) {
    t17 = <p className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-medium text-stone-500 sm:text-sm">{t15}{t16}</p>;
    $[80] = t16;
    $[81] = t17;
  } else {
    t17 = $[81];
  }
  let t18;
  if ($[82] !== analytics) {
    t18 = next_1 => {
      if (next_1) {
        analytics[AI_SEARCH_EVENTS.HISTORY_OPENED]({
          session_id: null,
          source: "hero"
        });
      }
      setHistoryOpen(next_1);
    };
    $[82] = analytics;
    $[83] = t18;
  } else {
    t18 = $[83];
  }
  let t19;
  let t20;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = cn("inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-stone-600 transition-colors", "hover:bg-stone-200 hover:text-stone-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary/60", "data-[state=open]:bg-stone-200 data-[state=open]:text-stone-900");
    t20 = <History className="size-3.5 shrink-0 opacity-80" strokeWidth={2} aria-hidden={true} />;
    $[84] = t19;
    $[85] = t20;
  } else {
    t19 = $[84];
    t20 = $[85];
  }
  let t21;
  if ($[86] !== i18n) {
    t21 = i18n._("activity.search.ai.historyTrigger", undefined, {
      message: "Previous chats"
    });
    $[86] = i18n;
    $[87] = t21;
  } else {
    t21 = $[87];
  }
  let t22;
  if ($[88] !== t21) {
    t22 = <PopoverTrigger asChild={true}><button type="button" className={t19}>{t20}{t21}</button></PopoverTrigger>;
    $[88] = t21;
    $[89] = t22;
  } else {
    t22 = $[89];
  }
  let t23;
  if ($[90] !== i18n) {
    t23 = i18n._("activity.search.ai.historyHeading", undefined, {
      message: "Recent chats"
    });
    $[90] = i18n;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  let t24;
  if ($[92] !== t23) {
    t24 = <div className="px-3 py-2"><p className="text-xs font-semibold text-foreground">{t23}</p></div>;
    $[92] = t23;
    $[93] = t24;
  } else {
    t24 = $[93];
  }
  let t25;
  if ($[94] !== aiChat || $[95] !== analytics || $[96] !== i18n || $[97] !== setMode || $[98] !== sortedThreads) {
    t25 = sortedThreads.length === 0 ? <p className="px-3 py-4 text-center text-xs text-muted-foreground">{i18n._("activity.search.ai.historyPreviewNotice", undefined, {
        message: "Preview only \u2014 history is not saved yet."
      })}</p> : <RecentChatsList threads={sortedThreads} activeThreadId={aiChat.chatId} showSearch={true} variant="popover" className="px-3 py-2" onSelect={id_0 => {
      analytics[AI_SEARCH_EVENTS.CHAT_RESUMED]({
        session_id: id_0,
        source: "hero_popover"
      });
      setHistoryOpen(false);
      setMode("ai");
      aiChat.openThread(id_0);
    }} />;
    $[94] = aiChat;
    $[95] = analytics;
    $[96] = i18n;
    $[97] = setMode;
    $[98] = sortedThreads;
    $[99] = t25;
  } else {
    t25 = $[99];
  }
  let t26;
  if ($[100] !== t24 || $[101] !== t25) {
    t26 = <PopoverContent align="end" sideOffset={8} className="w-[min(100vw-2rem,22rem)] p-0 shadow-lg">{t24}{t25}</PopoverContent>;
    $[100] = t24;
    $[101] = t25;
    $[102] = t26;
  } else {
    t26 = $[102];
  }
  let t27;
  if ($[103] !== historyOpen || $[104] !== t18 || $[105] !== t22 || $[106] !== t26) {
    t27 = <Popover open={historyOpen} onOpenChange={t18}>{t22}{t26}</Popover>;
    $[103] = historyOpen;
    $[104] = t18;
    $[105] = t22;
    $[106] = t26;
    $[107] = t27;
  } else {
    t27 = $[107];
  }
  let t28;
  if ($[108] !== t17 || $[109] !== t27) {
    t28 = <div className="mb-2 flex items-start justify-between gap-2 sm:items-center">{t17}{t27}</div>;
    $[108] = t17;
    $[109] = t27;
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  let t29;
  if ($[111] !== i18n) {
    t29 = i18n._("activity.search.ai.promptLabel", undefined, {
      message: "Describe your team building"
    });
    $[111] = i18n;
    $[112] = t29;
  } else {
    t29 = $[112];
  }
  let t30;
  if ($[113] !== t29) {
    t30 = <label className="sr-only" htmlFor="activity-ai-search-prompt">{t29}</label>;
    $[113] = t29;
    $[114] = t30;
  } else {
    t30 = $[114];
  }
  let t31;
  if ($[115] !== form || $[116] !== i18n) {
    t31 = field => <textarea id="activity-ai-search-prompt" name={field.name} value={field.state.value} onChange={e_0 => field.handleChange(e_0.target.value)} onBlur={field.handleBlur} onKeyDown={e_1 => {
      if (e_1.key === "Enter" && !e_1.shiftKey) {
        e_1.preventDefault();
        form.handleSubmit();
      }
    }} rows={3} placeholder={i18n._({
      id: "activity.search.ai.placeholder",
      message: "Describe your team building\u2026"
    })} className="w-full resize-none border-0 bg-transparent px-0 py-0 text-sm leading-snug text-stone-800 ring-0 transition-colors outline-none placeholder:text-stone-400 focus:ring-0 sm:text-base" />;
    $[115] = form;
    $[116] = i18n;
    $[117] = t31;
  } else {
    t31 = $[117];
  }
  let t32;
  if ($[118] !== form.Field || $[119] !== t31) {
    t32 = <form.Field name="prompt">{t31}</form.Field>;
    $[118] = form.Field;
    $[119] = t31;
    $[120] = t32;
  } else {
    t32 = $[120];
  }
  let t33;
  if ($[121] !== i18n) {
    t33 = i18n._("activity.search.ai.submit", undefined, {
      message: "Search team building"
    });
    $[121] = i18n;
    $[122] = t33;
  } else {
    t33 = $[122];
  }
  let t34;
  if ($[123] !== form || $[124] !== t33) {
    t34 = <div className="absolute right-3 bottom-3"><SubmitButton form={form} variant="tertiary" size="sm" className="rounded-full px-4 py-2 text-xs font-semibold shadow-sm sm:gap-2 sm:px-5 sm:text-sm">{t33}</SubmitButton></div>;
    $[123] = form;
    $[124] = t33;
    $[125] = t34;
  } else {
    t34 = $[125];
  }
  let t35;
  if ($[126] !== t14 || $[127] !== t28 || $[128] !== t30 || $[129] !== t32 || $[130] !== t34) {
    t35 = <div className={t14}>{t28}{t30}{t32}{t34}</div>;
    $[126] = t14;
    $[127] = t28;
    $[128] = t30;
    $[129] = t32;
    $[130] = t34;
    $[131] = t35;
  } else {
    t35 = $[131];
  }
  let t36;
  if ($[132] !== t11 || $[133] !== t12 || $[134] !== t35) {
    t36 = <form className={t11} onSubmit={t12}>{t35}</form>;
    $[132] = t11;
    $[133] = t12;
    $[134] = t35;
    $[135] = t36;
  } else {
    t36 = $[135];
  }
  return t36;
};
function _temp(s) {
  return s.threads;
}
function _temp2(a, b) {
  return b.createdAt - a.createdAt;
}